"use client";

import { useEffect, useRef, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Le petit sommaire des pages d'accélérateur, collé en haut de l'écran une
// fois le hero passé (il est rendu juste après lui). Six entrées au plus, la
// section en cours soulignée.
//
// Visiteur : il se cale sous l'en-tête de la page, qui porte déjà le bouton
// principal. Membre connecté : la page défile dans le conteneur de
// UserLayout, sous son propre en-tête ; la barre se colle en haut de ce
// conteneur et reprend le bouton, faute d'en-tête de page.
//
// La section en cours se lit par IntersectionObserver (sans racine : il
// fonctionne aussi dans un conteneur qui défile), et le clic passe par
// scrollIntoView, décalé par le scroll-margin des sections.
export default function AcceleratorSectionNav({ items, label, ctaLabel, onCta, loggedIn }) {
    const [active, setActive] = useState(null);
    const listRef = useRef(null);

    useEffect(() => {
        const sections = items
            .map((item) => document.getElementById(item.id))
            .filter(Boolean);
        if (!sections.length) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActive(entry.target.id);
                });
            },
            { rootMargin: "-35% 0px -60% 0px" },
        );
        sections.forEach((section) => observer.observe(section));
        return () => observer.disconnect();
    }, [items]);

    // Sur mobile, la pastille active reste visible dans la rangée.
    useEffect(() => {
        if (!active || !listRef.current) return;
        const chip = listRef.current.querySelector(`[data-id="${active}"]`);
        const list = listRef.current;
        if (!chip || list.scrollWidth <= list.clientWidth) return;
        list.scrollTo({
            left: chip.offsetLeft - list.clientWidth / 2 + chip.clientWidth / 2,
            behavior: "smooth",
        });
    }, [active]);

    const go = (event, id) => {
        const target = document.getElementById(id);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        setActive(id);
    };

    return (
        <nav
            aria-label={label}
            className={`sticky z-40 border-b border-slate-900/5 bg-white/85 backdrop-blur-xl ${
                loggedIn ? "top-0" : "top-16"
            }`}
        >
            <div className="mx-auto flex h-14 max-w-6xl items-center gap-4 px-6">
                <ul
                    ref={listRef}
                    className="-mx-6 mb-0 flex min-w-0 flex-1 items-center gap-1 overflow-x-auto px-3 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden"
                >
                    {items.map((item) => {
                        const isActive = active === item.id;
                        return (
                            <li key={item.id} className="shrink-0 list-none">
                                <a
                                    href={`#${item.id}`}
                                    data-id={item.id}
                                    onClick={(event) => go(event, item.id)}
                                    aria-current={isActive ? "location" : undefined}
                                    className={`relative inline-flex h-14 items-center px-3 text-sm transition-colors ${
                                        isActive
                                            ? "font-semibold text-slate-900"
                                            : "text-slate-500 hover:text-slate-900"
                                    }`}
                                >
                                    {item.label}
                                    <span
                                        aria-hidden
                                        className={`absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-primary transition-opacity duration-300 ${
                                            isActive ? "opacity-100" : "opacity-0"
                                        }`}
                                    />
                                </a>
                            </li>
                        );
                    })}
                </ul>
                {loggedIn && (
                    <button
                        type="button"
                        onClick={onCta}
                        className="hidden shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 sm:inline-flex"
                    >
                        {ctaLabel}
                        <SolarIcon name="arrow-right-linear" className="text-base" />
                    </button>
                )}
            </div>
        </nav>
    );
}
